<template>
  <div class="curator-image-editor bg-light" :class="{'min-h-100' : asset, 'h-100' : !asset}">
    <div v-if="asset">
      <div class="border p-1">
        <div class="d-flex justify-content-between flex-column">
          <div class="d-flex">
            <b-form-select :options="premiumSelectOptions" v-model="premium" plain class="mr-1 w-auto" :disabled="readonly"/>
            <b-form-select :options="usageSelectOptions" v-model="usage" plain class="mr-1 w-auto" :disabled="readonly"/>
            <b-form-select :options="exclusiveSelectOptions" v-model="exclusive" plain class="mr-1 w-auto" :disabled="readonly"/>
            <b-button variant="primary"
                      class="btn-rounded"
                      v-if="!readonly"
                      :disabled="saving"
                      @click="handleAssetSave">
              <span v-if="saving">Saving...</span>
              <span v-else>Save</span>
            </b-button>
          </div>
        </div>

        <div class="position-relative">

          <video-player
              v-if="isVideo"
              class="w-100 mt-2"
              style="max-height: 500px"
              :thumb-url="asset.thumb_url"
              :url="asset.asset_url"/>
          <b-img
              v-else
              :src="previewUrl"
              fluid
              class="w-100 mt-2"/>

          <b-modal size="xl" v-model="showPreviewModal"
                   @shown="loadImageViewer"
                   @hidden="unloadImageViewer"
                   hide-footer :title="asset.name">
            <video-player class="w-100 mt-2 d-flex flex-column justify-content-center"
                          style="height: 80vh;"
                          v-if="isVideo"
                          :thumb-url="asset.thumb_url"
                          :url="asset.asset_url"/>
            <div v-else class="w-100 mt-2" ref="previewContainer" style="height: 80vh;"/>
          </b-modal>

          <div class="position-absolute top-left">
            <slot name="image-preview-top-left"></slot>
          </div>

          <div class="position-absolute bottom-actions d-flex align-items-center justify-content-end"
              :class="{'video': isVideo }">
            <i class="fas fa-search-plus cursor-pointer text-white text-shadow-sm mr-2" v-b-tooltip
               title="Zoom" @click="showPreviewModal = true"></i>
            <file-input label="CHANGE ASSET" v-model="file" class="mr-2" v-if="!readonly">
              <template slot="button-slot">
                <i class="fas fa-edit cursor-pointer text-white text-shadow-sm" v-b-tooltip
                   title="Change Asset"></i>
              </template>
            </file-input>

            <a :href="asset.asset_url" target="_blank" class="text-white text-shadow-sm mr-2" v-b-tooltip
               title="Download Asset">
              <i class="fas fa-download"/>
            </a>

            <a v-if="isVideo" :href="asset.media_thumb_url" target="_blank" class="text-white text-shadow-sm"
               v-b-tooltip
               title="Download Watermarked Video">
              <i class="fas fa-download"/>
            </a>
            <a v-else-if="isIllustration" :href="asset.media_thumb_url" target="_blank"
               class="text-white text-shadow-sm"
               v-b-tooltip
               title="Download Watermarked Illustration">
              <i class="fas fa-download"/>
            </a>
          </div>
        </div>
      </div>

      <b-row v-if="errors">
        <b-col>
          <ul class="text-danger">
            <li v-for="error in errors">
              {{ error }}
            </li>
          </ul>
        </b-col>
      </b-row>

      <b-tabs class="mt-2" no-fade>
        <b-tab title="Keywords">
          <div class="bg-white p-2 border border-top-0">
            <b-form-group label="NAME" label-class="text-truncate">
              <b-textarea type="text" v-model="name" :disabled="readonly"/>
            </b-form-group>

            <b-form-group label="CAPTION" label-class="text-truncate">
              <b-input type="text" v-model="caption" :disabled="readonly"/>
            </b-form-group>

            <b-form-group label="COPYRIGHT" label-class="text-truncate">
              <b-input type="text" v-model="copyright" :disabled="readonly"/>
            </b-form-group>

            <b-form-group label="LOCATION" label-class="text-truncate">
              <b-input type="text" v-model="location" :disabled="readonly"/>
            </b-form-group>

            <tag-select field-name="CATEGORIES"
                        :tags="categories"
                        :multiple="true"
                        :select-picker-options="{maxOptions: 3}"
                        :disabled="readonly"
                        v-model="selectedCategories"/>

            <b-row>
              <b-col xl="6" lg="12"
                     v-for="tagCategoryData in tagsByCategoryArray">
                <tag-select :field-name="tagCategoryData.name"
                            :tags="tagCategoryData.data.tags"
                            :multiple="true"
                            :disabled="readonly"
                            :select-picker-options="{maxOptions: tagCategoryData.data.max_options}"
                            v-model="selectedTags[tagCategoryData.name]"/>
              </b-col>
            </b-row>

            <b-form-group label="KEYWORDS" class="mt-2">
              <select2 v-model="keywords"
                       :disabled="readonly"
                       :options="keywordOptions"
                       :settings="{ tags: true, multiple: true, placeholder: 'Add Keywords', tokenSeparators: [',', ';'] }"/>
            </b-form-group>
          </div>
        </b-tab>

        <b-tab title="File Info">
          <div class="bg-white p-1 border border-top-0">

            <div class="d-flex align-items-center mb-1" v-if="asset.published">
              <b-badge variant="secondary" class="rounded-0 mr-2">
                <div class="p-1 font-weight-semi-bold text-uppercase">Public URL</div>
              </b-badge>
              <a :href="`${assetPublicUrl(asset)}`" target="_blank" class="font-size-sm">{{ assetPublicUrl(asset) }}</a>
            </div>

            <div class="d-flex align-items-center mb-1">
              <b-badge variant="secondary" class="rounded-0 mr-2">
                <div class="p-1 font-weight-semi-bold text-uppercase">Submission URL</div>
              </b-badge>
              <a :href="`/curators/submissions/${asset.submission_id}`" target="_blank">Open</a>
            </div>

            <div class="d-flex align-items-center mb-1">
              <b-badge variant="secondary" class="rounded-0 mr-2">
                <div class="p-1 font-weight-semi-bold text-uppercase">Ref no:</div>
              </b-badge>
              <span>{{ asset.reference_number }}</span>
            </div>

            <div class="d-flex align-items-center mb-1">
              <b-badge variant="secondary" class="rounded-0 mr-2">
                <div class="p-1 font-weight-semi-bold text-uppercase">Asset ID:</div>
              </b-badge>
              <span>{{ asset.id }}</span>
            </div>

            <div v-for="(val, propName) in asset.metadata" class="d-flex align-items-center mb-1">
              <b-badge variant="secondary" class="rounded-0 mr-2">
                <div class="p-1 font-weight-semi-bold text-uppercase">{{ humanize(propName) }}</div>
              </b-badge>
              <span>{{ val }}</span>
            </div>
          </div>
        </b-tab>

        <b-tab title="Requests" v-if="!readonly">
          <div class="bg-white p-1 border border-top-0">
            <b-form-select :options="reasonsOptions" plain v-model="selectedReason" class="mb-2"/>

            <b-form-group>
              <label class="text-truncate">NOTES</label>
              <b-textarea v-model="notes"/>
            </b-form-group>

            <b-form-checkbox v-model="sendToContributor">
              Send to contributor
            </b-form-checkbox>
          </div>
        </b-tab>

        <b-tab title="Releases">
          <div class="bg-white p-1 border border-top-0">
            <label class="text-truncate">RELEASE FORMS</label>
            <EditReleaseForms :asset="asset"
                              v-model="releaseForms"
                              :disabled="readonly"
                              :initial-release-forms="submissionReleaseForms"
                              @releaseRequiredChanged="releaseRequired = arguments[0]"
                              @releaseFormsChanged="submissionReleaseForms = arguments[0]"
                              :release-required="releaseRequired"
                              :release-form-url="releaseFormUrl"
                              accepted-files="application/pdf"/>
          </div>
        </b-tab>
        <b-tab title="Activity">
          <div class="bg-white p-1 border border-top-0">
            <asset-versions :asset-id="asset.id"/>
          </div>
        </b-tab>
      </b-tabs>

      <div class="d-flex align-items-center justify-content-between mt-3">
        <div class="d-flex">
          <tag-select field-name="STATUS"
                      :tags="statusOptions"
                      v-if="!readonly"
                      :select-picker-options="{ style: 'btn-outline' }"
                      v-model="status"/>

          <b-form-group label="FEATURED" label-class="text-truncate" class="ml-2 mb-0">
            <b-form-radio-group v-model="ranked" name="ranked" class="mr-3">
              <b-form-radio value="true">Yes</b-form-radio>
              <b-form-radio value="false">No</b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <b-form-group label="AI Approved" label-class="text-truncate" class="ml-2 mb-0">
            <b-form-radio-group v-model="aiApproved" name="ai_approved" class="mr-3">
              <b-form-radio value="true">Yes</b-form-radio>
              <b-form-radio value="false">No</b-form-radio>
            </b-form-radio-group>
          </b-form-group>


          <div v-if="status === 'buyout'" class="ml-2">
            <b-form-group label="BUY OUT EXPIRES" label-class="text-truncate" class="mb-0">
              <date-range-picker
                  v-model="buyOutDateRange"
                  :ranges="false"
                  :single-date-picker="true"
                  :auto-apply="true"
                  :locale-data="pickerLocale"
                  ref="pickerInput"
              ></date-range-picker>
            </b-form-group>

            <div class="text-primary cursor-pointer" v-if="buyOutDateRange.startDate" @click="buyOutDateRange = makePickerEmptyDate()">Clear</div>
          </div>
        </div>

        <b-button variant="primary"
                  class="btn-rounded"
                  v-if="!readonly"
                  :disabled="saving"
                  @click="handleAssetSave">
          <span v-if="saving">Saving...</span>
          <span v-else>Save</span>
        </b-button>
      </div>


    </div>
    <div v-else class="d-flex h-100 justify-content-center align-items-center">
      Select an asset to edit
    </div>
  </div>

</template>

<script>
import TagSelect from "../../Common/TagSelect";
import AssetMixins from "../../../mixins/AssetMixins";
import GeneralMixins from "../../../mixins/GeneralMixins";
import AssetVersions from "./AssetVersions";
import FileInput from "../../Common/FileInput";
import ImageViewer from "iv-viewer";
import "iv-viewer/lib/image-viewer.css";
import select2 from 'v-select2-component';
import EditReleaseForms from "./EditReleaseForms";
import VideoPlayer from "../../Common/VideoPlayer";
import PreventNavigationMixins from "../../../mixins/PreventNavigationMixins";
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

export default {
  props: ['asset', 'categories', 'shouldPreventSaveLoss', 'url', 'tagCategoryOrder', 'releaseFormUrl', 'submissionReleaseForms', 'readonly'],
  components: {VideoPlayer, TagSelect, EditReleaseForms, AssetVersions, FileInput, select2, DateRangePicker},
  mixins: [AssetMixins, GeneralMixins, PreventNavigationMixins],

  model: {
    prop: 'asset',
    event: 'change'
  },

  watch: {
    asset(newAsset, oldImg) {
      const asset = newAsset || {}

      this.assetType = asset.asset_type
      this.name = asset.name
      this.caption = asset.caption
      this.copyright = asset.copyright
      this.location = asset.location
      this.selectedCategories = asset.category_ids || []
      this.selectedTags = this.getSelectedTags(asset, window.tagStore)
      this.keywords = asset.keywords || []
      this.keywordOptions = asset.keywords || []
      this.notes = asset.notes
      this.usage = asset.usage
      this.premium = asset.premium
      this.status = asset.status
      this.ranked = asset.rank_featured
      this.aiApproved = asset.ai_approved
      this.exclusive = asset.exclusive
      this.errors = null
      this.selectedReason = null
      this.sendToContributor = false
      this.releaseRequired = asset.release_required
      this.releaseForms = asset.release_forms || []
      this.buyOutDateRange = this.makePickerAssetDate(asset)

      this.$nextTick(() => {
        this.pendingChanges = false
      })
    },

    dataValues: function (val) {
      this.pendingChanges = true;
    },

    pendingChanges(val) {
      this.$emit('pendingChanges', val);
    }
  },

  data() {
    return {
      assetType: null,
      name: null,
      caption: null,
      copyright: null,
      location: null,
      selectedCategories: [],
      selectedTags: [],
      keywords: [],
      keywordOptions: [],
      status: null,
      ranked: false,
      aiApproved: false,
      notes: null,
      usage: null,
      premium: null,
      exclusive: null,
      file: null,
      errors: null,
      pendingChanges: false,
      selectedReason: null,
      sendToContributor: false,
      saving: false,
      showPreviewModal: false,
      releaseForms: [],
      releaseRequired: true,
      buyOutDateRange: this.makePickerEmptyDate()
    }
  },

  computed: {
    pickerLocale() {
      return {
        format: 'mm/dd/yyyy'
      }
    },

    isVideo() {
      return this.asset.asset_type === "Video"
    },

    isIllustration() {
      return this.asset.asset_type === "Illustration"
    },

    deactivateReasons() {
      return window.reasonStore.curatorAssetRemovalReasons
    },

    previewUrl() {
      return this.assetUpdatedAtImageUrl(this.asset.watermark_medium_url, this.asset)
    },

    tagsByCategory() {
      return window.tagStore.tagsByCategory;
    },

    tagsByCategoryArray() {
      if (this.tagCategoryOrder) {
        const sortedKeys = Object.keys(this.tagsByCategory).sort((t1, t2) => {
          const index1 = this.tagCategoryOrder.indexOf(t1);
          const index2 = this.tagCategoryOrder.indexOf(t2);
          return ((index1 > -1 ? index1 : Infinity) - (index2 > -1 ? index2 : Infinity));
        })

        return sortedKeys.map(k => {
          return {
            name: k,
            data: this.tagsByCategory[k]
          }
        })
      }

    },

    reasonsOptions() {
      return [null].concat(window.reasonStore.availableReasons(this.status)).map(k => {
        if (k) {
          return {value: k, text: k}
        } else {
          return {value: k, text: 'Select a reason'}
        }
      })
    },

    statusOptions() {
      return ['pending', 'approved', 'rejected', 'changes_requested', /*'buyout',*/ 'deactivated'].map(el => {
        return {
          id: el,
          name: this.humanize(el)
        }
      })
    },

    buyOutParamDate() {
      let start = this.buyOutDateRange.startDate
      if (start instanceof Date) {
        start = start.toISOString().split('T')[0]
      }
      return start || ""
    },

    assetFormData() {
      const dataParamName = this.assetType === "Photo" ? "data" : "video_data"

      let data = {
        name: this.name,
        caption: this.caption,
        copyright: this.copyright,
        location: this.location,
        category_ids: this.formDataArray(this.selectedCategories),
        tag_ids: this.formDataArray(this.formSelectedTags),
        release_form_ids: this.formDataArray(this.releaseForms.map(el => el.id)),
        release_required: this.releaseRequired,
        status: this.status,
        notes: this.reasonNotes,
        keywords: this.keywords,
        [dataParamName]: this.file,
        exclusive: this.exclusive,
        usage: this.usage,
        premium: this.premium,
        send_to_contributor: this.sendToContributor,
        rank_featured: this.ranked,
        ai_approved: this.aiApproved
      }

      if (data.status === 'buyout') {
        data['buyout_expires_at'] = this.buyOutParamDate
      }

      return data
    },

    usageSelectOptions() {
      return ['editorial', 'commercial'].map(el => {
        return {
          value: el,
          text: this.humanize(el)
        }
      })
    },

    exclusiveSelectOptions() {
      return [true, false].map(el => {
        return {
          value: el,
          text: el ? 'Exclusive' : 'Non-Exclusive'
        }
      })
    },

    premiumSelectOptions() {
      return [true, false].map(el => {
        return {
          value: el,
          text: el ? 'Premium' : 'Standard'
        }
      })
    },

    dataValues() {
      return this.name,
          this.caption,
          this.copyright,
          this.location,
          this.selectedCategories,
          this.selectedTags,
          this.keywords,
          this.notes,
          this.usage,
          this.premium,
          this.exclusive,
          this.sendToContributor,
          this.selectedReason,
          this.releaseForms,
          this.releaseRequired,
          this.buyOutDateRange,
          this.ranked,
          this.aiApproved,
          Date.now();
    },

    reasonNotes() {
      if (this.selectedReason) {
        if (this.notes && this.notes.length > 0) {
          return `${this.selectedReason}\n\n${this.notes}`
        } else {
          return this.selectedReason;
        }
      }
      return this.notes;
    }
  },

  beforeDestroy() {
    if (this.imageViewer) {
      this.imageViewer.destroy();
      this.imageViewer = null;
    }
  },

  methods: {

    makePickerEmptyDate() {
      return {
        startDate: null,
        endDate: null
      }
    },

    makePickerAssetDate(asset) {
      let date = null
      if (asset.buyout_expires_at) {
        date = new Date(asset.buyout_expires_at)
      }

      return {
        startDate: date,
        endDate: date
      }
    },

    loadImageViewer() {
      if (!this.isVideo) {
        if (!this.imageViewer) {
          const asset = this.$refs['previewContainer'];
          this.imageViewer = new ImageViewer(asset, {});
        }
        const assetUrl = this.isIllustration ? this.previewUrl : this.asset.asset_url
        // TODO: the asset should define a large image preview URL
        this.imageViewer.load(this.previewUrl, assetUrl);
      }
    },

    unloadImageViewer() {
      if (this.imageViewer) {
        this.imageViewer.destroy();
        this.imageViewer = null;
      }
    },

    updateTagsWithAsset(asset) {
      this.keywords = asset.keywords || [];
      this.keywordOptions = asset.keywords || [];
      this.asset.keywords = asset.keywords;
      this.asset.tag_ids = asset.tag_ids;
    },

    handleAssetSave: function () {
      this.saveAsset(this.assetFormData, false).then(response => {
        if (response.status < 400) {
          const asset = this.buildAssetFromResponse(response.body);
          this.$emit('change', asset);
          this.showToast('Asset was updated', 'Asset Edit');
        } else {
          this.errors = this.getErrorMessages(response.body);
        }
        this.file = null;

        this.pendingChanges = false;
      })
    },

    updateWithAsset(asset) {
      this.updateTagsWithAsset(asset);
    },
  },

  mounted: function () {
    window.POC.sidebar.collapsed = true;

    if (this.shouldPreventSaveLoss) {
      this.preventSaveLoss(() => {
        return this.pendingChanges;
      });
    }
  },
}
</script>